<template>
  <div class="retention-card" v-loading="loading">
    <div class="retention-card-header">
      <span class="retention-card-title">{{ title }}</span>
      <span
        v-if="statusBadge && !showNoPolicy && (isScheduled || record)"
        class="retention-status-badge"
        :class="statusBadge.className"
      >
        {{ statusBadge.label }}
      </span>
    </div>

    <template v-if="!loading">
      <div v-if="showNoPolicy" class="retention-card-info">
        <div class="retention-card-info-title">
          {{ $t('DataRetention_Overview_NoPolicy_Title') }}
        </div>
        <div class="retention-card-info-description">
          {{ $t('DataRetention_Overview_NoPolicy_Description') }}
        </div>
      </div>

      <template v-else>
        <div v-if="!isScheduled && !record" class="retention-card-info">
          <div class="retention-card-info-title">
            {{ $t('DataRetention_Overview_NoDataYet_Title') }}
          </div>
          <div class="retention-card-info-description">
            {{ noDataDescription }}
          </div>
        </div>

        <template v-else>
          <div class="retention-card-policy">
            <div class="retention-card-policy-name">{{ policyName }}</div>
          </div>

          <div
            v-if="isScheduled && nextRunText"
            class="retention-card-run-time"
          >
            <span class="retention-card-run-time-label"
              >{{ $t('DataRetention_Overview_NextRun') }}:</span
            >
            <span class="retention-card-run-time-value">{{ nextRunText }}</span>
          </div>
          <div v-if="isScheduled" class="retention-card-run-time">
            <span class="retention-card-run-time-label"
              >{{ $t('DataRetention_Overview_LastCalculated') }}:</span
            >
            <span class="retention-card-run-time-value">{{
              lastCalculatedText || '-'
            }}</span>
          </div>
          <div v-if="!isScheduled" class="retention-card-run-time">
            <span class="retention-card-run-time-label"
              >{{ $t('DataRetention_Overview_ExecutedAt') }}:</span
            >
            <span class="retention-card-run-time-value">{{
              executedAtText || '-'
            }}</span>
          </div>

          <div v-if="isScheduled && !record" class="retention-card-info">
            <div class="retention-card-info-title">
              {{ $t('DataRetention_Overview_NoDataYet_Title') }}
            </div>
            <div class="retention-card-info-description">
              {{ noDataDescription }}
            </div>
          </div>
        </template>

        <template v-if="record">
          <div
            v-if="showHighDeletionWarning && !warningDismissed"
            class="retention-card-warning"
          >
            <i class="el-icon-warning retention-card-warning-icon"></i>
            <div class="retention-card-warning-body">
              <span class="retention-card-warning-title">
                {{ $t('DataRetention_Overview_HighImpactAlert_Title') }}
              </span>
              <span class="retention-card-warning-message">
                {{ highImpactAlertMessage }}
              </span>
            </div>
            <button
              type="button"
              class="retention-card-warning-close"
              :aria-label="$t('Button_Close')"
              :data-qa="`data-retention-${cardKind}-warning-close`"
              @click="warningDismissed = true"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>

          <div class="retention-card-summary">
            {{ summaryText }}
          </div>

          <div class="retention-card-breakdown">
            <div
              v-for="category in categories"
              :key="category.key"
              class="retention-card-row"
            >
              <span class="retention-card-row-label">{{ category.label }}</span>
              <span class="retention-card-row-value">
                {{ formatNumber(breakdownValue(category.key)) }}
              </span>
            </div>
          </div>
        </template>

        <div v-if="record" class="retention-card-actions">
          <button
            type="button"
            class="retention-card-link"
            :disabled="downloading"
            :data-qa="`data-retention-${cardKind}-download`"
            @click="onDownload"
          >
            <i v-if="downloading" class="el-icon-loading"></i>
            <span v-else>{{
              $t('DataRetention_Overview_DownloadReport')
            }}</span>
          </button>
          <el-button
            type="primary"
            plain
            size="small"
            class="retention-card-history-button"
            :data-qa="`data-retention-${cardKind}-view-history`"
            @click="onViewHistory"
          >
            {{ $t('DataRetention_Overview_ViewHistory') }}
          </el-button>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import RestApi from '@/core/services/RestApi'
import { projectRoute, projectUrl } from '@/core/helpers'
import {
  HISTORY_IDENTIFIERS,
  POLICY_LABEL_KEYS,
  RETENTION_TYPES
} from '@/modules/settings/enum/DataRetentionEnums'
import dataRetentionMixin from '@/modules/settings/mixins/dataRetentionMixin'

export default {
  name: 'RetentionExecutionCard',

  mixins: [dataRetentionMixin],

  props: {
    cardKind: {
      type: String,
      required: true,
      validator: v => ['scheduled', 'live'].includes(v)
    },
    title: {
      type: String,
      required: true
    },
    retentionType: {
      type: String,
      required: true
    },
    categories: {
      type: Array,
      required: true
    },
    record: {
      type: Object,
      default: null
    },
    policy: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    policyActive: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      downloading: false,
      warningDismissed: false
    }
  },

  watch: {
    record() {
      this.warningDismissed = false
    }
  },

  computed: {
    isScheduled() {
      return this.cardKind === 'scheduled'
    },

    historyIdentifierFilter() {
      const identifiers = HISTORY_IDENTIFIERS[this.retentionType]
      if (!identifiers) {
        return null
      }

      return this.isScheduled ? identifiers.preview : identifiers.live
    },

    showNoPolicy() {
      return this.isScheduled && !this.policyActive
    },

    noDataDescription() {
      return this.isScheduled
        ? this.$t('DataRetention_Overview_NoPreview_Description')
        : this.$t('DataRetention_Overview_NoLive_Description')
    },

    policyName() {
      const key = POLICY_LABEL_KEYS[this.retentionType]
      return key ? this.$t(key) : ''
    },

    breakdown() {
      const result = (this.record && this.record.result) || {}
      return this.normalizeRetentionBreakdown(this.retentionType, result)
    },

    showHighDeletionWarning() {
      return this.hasHighDeletionWarning(this.record)
    },

    highImpactAlertMessage() {
      const percent = this.getHighDeletionImpactPercent(this.record)
      if (percent == null) {
        return this.$t(
          'DataRetention_Overview_HighImpactAlert_Message_Fallback'
        )
      }

      if (this.retentionType === RETENTION_TYPES.USER_PROFILES) {
        return this.$t(
          'DataRetention_Overview_HighImpactAlert_Message_UserProfiles',
          { percent }
        )
      }

      return this.$t('DataRetention_Overview_HighImpactAlert_Message_Generic', {
        percent
      })
    },

    totalCount() {
      return this.computeRetentionTotal(this.record, this.breakdown)
    },

    summaryText() {
      if (this.isScheduled) {
        const activePolicies = this.countActivePolicies(
          this.retentionType,
          this.policy
        )
        return this.$t('DataRetention_Overview_EstimatedImpact', {
          count: activePolicies
        })
      }

      return this.$t('DataRetention_Overview_TotalRecordsRemoved', {
        count: this.formatNumber(this.totalCount)
      })
    },

    statusBadge() {
      if (this.isScheduled) {
        return {
          label: this.$t('DataRetention_Overview_Status_Scheduled'),
          className: 'scheduled'
        }
      }

      const status = this.getExecutionStatus(this.record)
      if (!status) {
        return null
      }

      return {
        label:
          status === 'success'
            ? this.$t('DataRetention_Overview_Status_Success')
            : this.$t('DataRetention_Overview_Status_Failed'),
        className: status
      }
    },

    nextRunText() {
      const nextRun = this.getNextRunTimestamp(this.retentionType, this.policy)
      return nextRun ? this.formatDateTime(nextRun, false) : ''
    },

    lastCalculatedText() {
      const lastCalculated = this.record && this.record.last_calculated
      return lastCalculated ? this.formatDateTime(lastCalculated, true) : ''
    },

    executedAtText() {
      const finishTime = this.record && this.record.finish_time
      return finishTime ? this.formatDateTime(finishTime, true) : ''
    }
  },

  methods: {
    breakdownValue(key) {
      const value = this.breakdown[key]
      return typeof value === 'number' ? value : 0
    },

    formatNumber(value) {
      return Number(value).toLocaleString('en-US')
    },

    formatDateTime(timestamp, includeTime) {
      if (!timestamp) {
        return ''
      }

      const date = moment.unix(timestamp)
      const project = this.$store.state.app.project
      if (project && project.timezone) {
        date.tz(project.timezone)
      }

      return includeTime
        ? date.format('D MMM YYYY HH:mm')
        : date.format('D MMM YYYY')
    },

    onDownload() {
      if (!this.record || this.downloading) {
        return
      }
      const taskId = this.record.task_id
      const reportType = this.record.identifier
      if (!taskId || !reportType) {
        return
      }

      this.downloading = true

      RestApi.get(projectUrl('retention-policy/report'), {
        params: { type: reportType, task_id: taskId }
      })
        .then(response => {
          const url = response && response.data && response.data.url
          if (!url) {
            throw new Error(this.$t('RestApi_Error_Unknown'))
          }
          const link = document.createElement('a')
          link.href = url
          link.target = '_blank'
          link.rel = 'noopener'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch(error => {
          const message =
            (error && error.message) || this.$t('RestApi_Error_Unknown')
          this.$message.error(message)
        })
        .finally(() => {
          this.downloading = false
        })
    },

    onViewHistory() {
      if (!this.historyIdentifierFilter) {
        return
      }

      this.$router.push(
        projectRoute({
          name: 'DataRetentionHistory',
          query: {
            filters: JSON.stringify({
              identifier: this.historyIdentifierFilter
            })
          }
        })
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.retention-card {
  border: 1px solid #dbdee7;
  border-radius: 4px;
  padding: 20px;
  background: #fff;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.retention-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.retention-card-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #2c3e50;
}

.retention-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;

  &.scheduled {
    background: #f3ecff;
    color: #7b4fd6;
  }

  &.success {
    background: #f0f9eb;
    color: #67c23a;
  }

  &.failed {
    background: #fef0f0;
    color: #f56c6c;
  }
}

.retention-card-info {
  padding: 12px 0;
}

.retention-card-info-title {
  font-weight: 600;
  color: #4b515f;
  margin-bottom: 4px;
}

.retention-card-info-description {
  color: #7e8596;
  font-size: 14px;
}

.retention-card-policy-name {
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: #2c3e50;
}

.retention-card-run-time {
  font-size: 14px;
  line-height: 22px;
  color: #5c6374;
}

.retention-card-run-time-label {
  font-weight: 700;
  margin-right: 4px;
}

.retention-card-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background-color: #fff8ef;
  border-left: 4px solid #e6a23c;
  border-radius: 4px;
}

.retention-card-warning-icon {
  color: #e6a23c;
  font-size: 18px;
  line-height: 22px;
  margin-top: 1px;
}

.retention-card-warning-body {
  flex: 1;
  font-size: 14px;
  line-height: 22px;
}

.retention-card-warning-title {
  font-weight: 600;
  color: #e6a23c;
  margin-right: 6px;
}

.retention-card-warning-message {
  color: #5c6374;
}

.retention-card-warning-close {
  border: none;
  background: transparent;
  color: #c58a2d;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  .el-icon-close {
    font-size: 16px;
  }
}

.retention-card-summary {
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: #2c3e50;
}

.retention-card-breakdown {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.retention-card-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 14px;
  line-height: 22px;
  color: #5c6374;
}

.retention-card-row-value {
  font-weight: 600;
  color: #2c3e50;
}

.retention-card-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding-top: 8px;
}

.retention-card-link {
  border: none;
  background: transparent;
  color: #1d87c8;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/components/RetentionExecutionCard.vue